/**
 * The way back to Draft, behind a dialog that demands a reason.
 *
 * @module components/governance/ReturnToDraft
 */

import React, { useState } from "react";

import { Alert, Button, Modal, Text } from "@/components/ui";
import { FormField, TextAreaField } from "@/components/ui/Form";
import type { WorkflowState } from "@/server/types";

export interface ReturnToDraftProps {
  workflowState: WorkflowState;
  actionPending: boolean;
  hasUnsavedChanges: boolean;
  error: Error | null;
  /** Why the server would refuse the return, when it would. */
  refusal?: string;
  /** Resolves `true` once the programme is back in Draft. */
  onReturn: (reason: string) => Promise<boolean>;
}

export function ReturnToDraft({
  workflowState,
  actionPending,
  hasUnsavedChanges,
  error,
  refusal = undefined,
  onReturn,
}: ReturnToDraftProps): React.ReactElement {
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState("");
  let transientRefusal: string | undefined;
  if (actionPending) {
    transientRefusal = "Wait for the action in progress to finish.";
  } else if (hasUnsavedChanges) {
    transientRefusal = "Unavailable while there are unsaved changes";
  }
  // The server's refusal first: waiting would not lift it.
  const triggerRefusal = refusal ?? transientRefusal;
  const close = (): void => {
    setOpen(false);
    setReason("");
  };

  return (
    <div className="mt-4" data-testid="return-to-draft">
      <Text tone="muted">
        You can send this programme back to Draft, which reopens it for editing.
      </Text>
      <Button
        size="sm"
        variant="outline"
        tone="danger"
        icon="arrow-u-up-left"
        className="mt-2"
        disabled={triggerRefusal !== undefined}
        disabledReason={triggerRefusal}
        onClick={() => setOpen(true)}
        data-testid="return-to-draft-open"
      >
        Return to Draft
      </Button>
      {open ? (
        <Modal
          open
          tone="danger"
          title="Return this programme to Draft?"
          subtitle={`It leaves ${workflowState} and reopens for editing. Review decisions, any approval and its sign-offs no longer count, and it must be submitted for internal review again.`}
          onClose={close}
          closeRefusal={actionPending ? "The programme is being returned to Draft" : undefined}
          data-testid="return-to-draft-dialog"
          footer={
            <>
              <Button
                variant="ghost"
                disabled={actionPending}
                disabledReason="Wait for the action in progress to finish."
                onClick={close}
              >
                Cancel
              </Button>
              <Button
                variant="primary"
                tone="danger"
                icon="arrow-u-up-left"
                loading={actionPending}
                loadingLabel="Returning to Draft"
                disabled={!reason.trim()}
                disabledReason="Give a reason for returning to Draft"
                onClick={() => {
                  onReturn(reason.trim())
                    .then((landed) => {
                      if (landed) {
                        close();
                      }
                    })
                    .catch(() => undefined);
                }}
                data-testid="return-to-draft-submit"
              >
                Return to Draft
              </Button>
            </>
          }
        >
          <FormField
            label="Reason"
            htmlFor="return-to-draft-reason"
            helpText="Recorded in the programme's audit trail."
          >
            <TextAreaField
              id="return-to-draft-reason"
              value={reason}
              onChange={setReason}
              rows={4}
              className="w-full"
              data-testid="return-to-draft-reason"
            />
          </FormField>
          {/* The dialog covers the panel's own alert. */}
          {error ? (
            <Alert tone="warning" className="mt-4" data-testid="return-to-draft-error">
              {error.message}
            </Alert>
          ) : null}
        </Modal>
      ) : null}
    </div>
  );
}
